import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ErrorMessage from '../../../components/forms/ErrorMessage';
import SubmitButton from '../../../components/forms/SubmitButton';
import TextInput from '../../../components/forms/TextInput';

import { useAuth } from '../../../hooks/useAuth';
import { useForm } from '../../../hooks/useForm';
import { useScrollToTop } from '../../../hooks/useScrollToTop';

function LoginPage() {
    // Router navigation
    const navigate = useNavigate();
    const scrollToTop = useScrollToTop();

    // Auth hook
    const { refresh } = useAuth();

    // Local state
    const [step, setStep] = useState('phone'); // "phone" | "pin"
    const [phoneValue, setPhoneValue] = useState('');
    const [apiError, setApiError] = useState(null);
    const [isSending, setIsSending] = useState(false);

    // Forms for each step
    const phoneForm = useForm({ defaultValues: { phone: '' } });
    const pinForm = useForm({ defaultValues: { pin: '' } });

    // Handler for phone number submission
    const onSubmitPhone = async data => {
        setApiError(null);
        setIsSending(true);

        try {
            const res = await fetch('/api/auth/login.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: data.phone })
            });

            const json = await res.json();
            if (!json.success) throw new Error(json.message || 'Noe gikk galt, prøv igjen senere');

            setPhoneValue(data.phone);
            setStep('pin');
            scrollToTop('smooth');
        } catch (err) {
            setApiError(err.message);
        } finally {
            setIsSending(false);
        }
    };

    // Handler for PIN submission
    const onSubmitPin = async data => {
        setApiError(null);
        setIsSending(true);

        try {
            const res = await fetch('/api/auth/login_ok.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: phoneValue, pin: data.pin })
            });

            const json = await res.json();
            if (!json.success) throw new Error(json.message || 'Noe gikk galt, prøv igjen senere');

            await refresh();
            document.dispatchEvent(
                new CustomEvent('sar61:auth-changed', {
                    detail: { loggedIn: true }
                })
            );
            navigate('/', { replace: true });
            scrollToTop('smooth');
        } catch (err) {
            setApiError(err.message);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="minside-page minside-auth">
            <div className="container container--narrow">
                <header className="minside-header">
                    <h1 className="minside-title">Min side</h1>
                    <p className="minside-subtitle">
                        Meld deg på arrangementet og administrer ditt opphold.
                    </p>
                </header>

                <div className="minside-card">
                    {step === 'phone' && (
                        <form
                            onSubmit={phoneForm.handleSubmit(onSubmitPhone)}
                            className="minside-form"
                        >
                            <h4>Logg inn / Registrer deg</h4>
                            <p className="minside-note">
                                Første gang du logger inn må du registrere deg som deltaker. Når
                                registreringen er fullført kan du velge hvilke aktiviteter du vil
                                delta på.
                            </p>
                            <TextInput
                                autoFocus
                                label="Mobilnummer*"
                                name="phone"
                                type="tel"
                                autocomplete="tel"
                                error={phoneForm.formState.errors.phone}
                                disableTab={phoneForm.formState.isSubmitSuccessful}
                                hint="Skriv inn ditt mobilnummer uten mellomrom og landekode. Du vil
                                motta en engangskode på SMS som du kan bruke i det neste steget."
                                {...phoneForm.register('phone', {
                                    required: 'Mobilnummer er påkrevd.',
                                    minLength: { value: 8, message: 'Mobilnummeret er for kort.' },
                                    maxLength: {
                                        value: 12,
                                        message: 'Mobilnummeret er for langt.'
                                    },
                                    pattern: {
                                        value: /^[0-9]+$/,
                                        message: 'Mobilnummeret kan kun inneholde tall.'
                                    }
                                })}
                            />
                            <div className="form-submit-row form-submit-row--auth">
                                <div className="form-submit-row__message">
                                    {apiError && <ErrorMessage message={apiError} />}
                                </div>

                                <div className="form-submit-row__actions">
                                    <SubmitButton
                                        isLoading={phoneForm.formState.isSubmitting}
                                        disableTab={phoneForm.formState.isSubmitSuccessful}
                                    >
                                        Send engangskode
                                    </SubmitButton>
                                </div>
                            </div>
                        </form>
                    )}

                    {step === 'pin' && (
                        <form onSubmit={pinForm.handleSubmit(onSubmitPin)} className="minside-form">
                            <h4>Verifiser engangskode</h4>
                            <p className="minside-note">
                                Vi har sendt en engangskode på SMS til deg. Hvis du ikke har
                                registrert ditt nummer tidligere, vil du bli bedt om å registrere
                                deg for påmelding etter at koden er verifisert.
                            </p>
                            <TextInput
                                autoFocus
                                label="Engangskode*"
                                name="pin"
                                type="text"
                                autocomplete="one-time-code"
                                error={pinForm.formState.errors.pin}
                                disableTab={pinForm.formState.isSubmitSuccessful}
                                hint="Skriv inn den 4-sifrede koden du mottok på SMS."
                                {...pinForm.register('pin', {
                                    required: 'Engangskode er påkrevd.',
                                    minLength: { value: 4, message: 'Koden er for kort.' },
                                    maxLength: {
                                        value: 4,
                                        message: 'Koden er for lang.'
                                    },
                                    pattern: {
                                        value: /^[0-9]+$/,
                                        message: 'Koden kan kun inneholde tall.'
                                    }
                                })}
                            />
                            <div className="form-submit-row form-submit-row--auth">
                                <div className="form-submit-row__message">
                                    {apiError && <ErrorMessage message={apiError} />}
                                </div>

                                <div className="form-submit-row__actions">
                                    <SubmitButton
                                        isLoading={pinForm.formState.isSubmitting}
                                        disableTab={pinForm.formState.isSubmitSuccessful}
                                    >
                                        Verifiser kode
                                    </SubmitButton>
                                    <button
                                        className="btn-outline"
                                        type="button"
                                        onClick={() => {
                                            setApiError(null);
                                            setStep('phone');
                                        }}
                                        disabled={isSending}
                                    >
                                        Tilbake
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
